import type { PopoverProps } from '@nuxt/ui';
import type { MaybeRefOrGetter } from 'vue';
import { useTextDirection } from '@vueuse/core';
import { computed, toValue } from 'vue';
import { preventOutsideDismissWithinMenu } from '#layers/ui/components/NestedSelectMenu/utils';
import { isInsideBreadcrumbEllipsisPanel } from '#layers/ui/utils/is-inside-breadcrumb-ellipsis-panel';
import { isInsideClosedPopoverTrigger } from '#layers/ui/utils/reka-popper';

/**
 * The popover lives inside the `…` panel, but reka treats clicks and focus there as "outside" and
 * would close it. Keep it open within the panel; close it only when another switcher opens.
 */
function preventPanelDismiss(event: Event) {
	preventOutsideDismissWithinMenu(event, (target) =>
		isInsideBreadcrumbEllipsisPanel(target) && !isInsideClosedPopoverTrigger(target));
}

const inlineContent: PopoverProps['content'] = { align: 'start' };

export function useFoldedSwitcherPopover(folded: MaybeRefOrGetter<boolean> = false) {
	const dir = useTextDirection();

	// RTL-aware position for a folded switcher: opens to the inline-end side like a nested submenu.
	// `prioritizePosition` + `sticky: 'always'` let reka pull it fully into view when neither side has
	// room (plain `shift` only moves it on the cross axis).
	const foldedPosition = computed<PopoverProps['content']>(() => ({
		side: dir.value === 'rtl' ? 'left' : 'right',
		align: 'start',
		sideOffset: 8,
		collisionPadding: 8,
		avoidCollisions: true,
		prioritizePosition: true,
		sticky: 'always',
	}));

	const foldedContent = computed<PopoverProps['content']>(() => ({
		...foldedPosition.value,
		onPointerDownOutside: preventPanelDismiss,
		onFocusOutside: (event: Event) => event.preventDefault(),
		onInteractOutside: preventPanelDismiss,
	}));

	const switcherContent = computed<PopoverProps['content']>(() =>
		toValue(folded) ? foldedContent.value : inlineContent);

	return { foldedPosition, switcherContent };
}
